import { getRouteApi } from '@tanstack/react-router';
import { Copy, Info } from 'lucide-react';
import React from 'react';
import { toast } from 'react-toastify';
import { DiscordProfileLink } from '~/partials/DiscordProfileLink';
import { Dialog, DialogContent, DialogTitle } from '~/primitives/dialog';
import { ModalProps } from '~/util';
import { useLocaleDateTimeString } from '~/util/hooks/useLocale';

const Route = getRouteApi('/profile/user/$userid');

const InfoField = ({ label, value }: { label: string; value: string }) => (
    <div className="flex flex-col gap-0">
        <label htmlFor={label}>{label}:</label>
        <div className="flex gap-2 items-center">
            <p
                id={label}
                className="font-normal text-xl bg-background-dark rounded-md px-2 py-0.5"
            >
                {value}
            </p>
            <Copy
                onClick={() => {
                    navigator.clipboard.writeText(value);
                    toast.info('Value copied to clipboard');
                }}
                className="h-4 w-4 cursor-pointer text-secondary-foreground hover:text-secondary-light transition-colors"
            />
        </div>
    </div>
);

export const UserInfoDialog: React.FC<ModalProps> = ({ ...modalProps }) => {
    const profile = Route.useLoaderData({
        select: (data) => data.profile,
    });

    const localeDateTime = useLocaleDateTimeString();

    return (
        <Dialog {...modalProps}>
            <DialogContent className="w-2xl" aria-describedby={undefined}>
                <DialogTitle>User information</DialogTitle>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-8">
                        <InfoField
                            label="Global Name"
                            value={profile.global_name}
                        />
                        <InfoField label="Username" value={profile.username} />
                    </div>

                    <InfoField label="AREDL UUID" value={profile.id} />
                    <InfoField
                        label="Account creation date"
                        value={localeDateTime(profile.created_at)}
                    />
                    {profile.placeholder ? (
                        <p className=" text-secondary-foreground bg-pending-transparent border-pending border rounded-md p-2">
                            <Info className="inline h-4 w-4" /> This user is a
                            placeholder account and is not associated with a
                            real discord user.
                        </p>
                    ) : profile.discord_id ? (
                        <div className="flex gap-4 items-center">
                            <DiscordProfileLink id={profile.discord_id} />
                            <InfoField
                                label="Discord ID"
                                value={profile.discord_id}
                            />
                        </div>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
};
